import { logger } from 'src/logger';
import { PreviewAssets } from 'src/types';
import {
  loadImage,
  calculateCenterCrop,
  upscaleImage,
  fitWithin,
  wrapText,
  resolveTextPosition,
} from 'src/utils/preview/canvasUtils';
import {
  ShellLayer,
  ResizeTransform,
  FitWithinTransform,
  GradientLayer,
  TextLayer,
  GradientDirection,
  PreviewStateConfig,
} from 'src/utils/preview/types';

/**
 * Render image at full size starting at (0, 0)
 * Used for device shell overlays.
 */
export const renderImage = async (
  ctx: CanvasRenderingContext2D,
  layer: ShellLayer,
): Promise<void> => {
  try {
    const image = await loadImage(layer.src);
    ctx.drawImage(image, 0, 0);
  } catch (error) {
    logger.error('Failed to render image', { error, src: layer.src });
    throw error;
  }
};

/**
 * Render image with CENTER_CROP transform (cover fit)
 * Scales to fill target area, crops overflow based on shiftFactor.
 */
export const renderImageCenterCrop = async (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  offsetX = 0,
  offsetY = 0,
  shiftFactor = 0.5,
): Promise<void> => {
  const crop = calculateCenterCrop(
    image.width,
    image.height,
    targetWidth,
    targetHeight,
    offsetX,
    offsetY,
    shiftFactor,
  );
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height);
};

/**
 * Render image with RESIZE transform
 * Draws at exact dimensions, upscales if needed.
 * 3. Draw at specified position and dimensions
 *
 * @param ctx - Canvas rendering context to draw on
 * @param image - Loaded image element to render
 * @param transform - Resize transform with position and dimensions
 * @param transform.x - X position in pixels (default: 0)
 * @param transform.y - Y position in pixels (default: 0)
 * @param transform.width - Target width in pixels (default: image width)
 * @param transform.height - Target height in pixels (default: image height)
 *
 * @example
 * // Resize background to exact dimensions at specific position
 * await renderImageResize(ctx, bgImage, {
 *   type: BackgroundTransformType.RESIZE,
 *   x: 100,
 *   y: 200,
 *   width: 800,
 *   height: 600
 * });
 * // Result: Image drawn at (100, 200) sized to 800x600, upscaled if needed
 */
export const renderImageResize = async (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  transform: ResizeTransform,
): Promise<void> => {
  const width = transform.width;
  const height = transform.height;
  let finalImage = image;

  // Upscale if dimensions are larger than original
  if (width && height && (width > image.width || height > image.height)) {
    const upscaledUrl = await upscaleImage(image, width, height);
    finalImage = await loadImage(upscaledUrl);
  }

  ctx.drawImage(
    finalImage,
    transform.x || 0,
    transform.y || 0,
    width || finalImage.width,
    height || finalImage.height,
  );
};

/**
 * Render image with FIXED transform
 * Draws at exact coordinates and dimensions.
 */
export const renderImageFixed = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
): void => {
  ctx.drawImage(image, x, y, width, height);
};

/**
 * Render image with FIT_WITHIN transform (contain fit)
 * Scales to fit within bounds, maintains aspect ratio.
 */
export const renderImageFitWithin = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  transform: FitWithinTransform,
): void => {
  const fit = fitWithin(image.width, image.height, transform.maxWidth, transform.maxHeight, {
    positionX: transform.position.x,
    positionY: transform.position.y,
  });
  ctx.drawImage(image, fit.x, fit.y, fit.width, fit.height);
};

/**
 * Render gradient overlay layer
 * Creates linear gradients (horizontal or vertical) with color stops.
 */
export const renderGradient = (
  ctx: CanvasRenderingContext2D,
  layer: GradientLayer,
  config: PreviewStateConfig,
): void => {
  const { direction, colorStops, bounds } = layer;

  let gradient: CanvasGradient;
  if (direction === GradientDirection.HORIZONTAL) {
    gradient = ctx.createLinearGradient(bounds.x, bounds.y, bounds.x + bounds.width, bounds.y);
  } else {
    // VERTICAL
    gradient = ctx.createLinearGradient(bounds.x, bounds.y, bounds.x, bounds.y + bounds.height);
  }

  colorStops.forEach((stop) => gradient.addColorStop(stop.position, stop.color));
  ctx.fillStyle = gradient;
  ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
};

/**
 * Render text layer with wrapping
 * Wraps text to fit maxWidth, adds ellipsis if truncated.
 */
export const renderText = (
  ctx: CanvasRenderingContext2D,
  layer: TextLayer,
  assets: PreviewAssets,
): void => {
  const bodyCopy = assets.bodyCopy;
  if (!bodyCopy) return;

  ctx.font = `${layer.fontSize}px ${layer.fontFamily}`;
  ctx.fillStyle = layer.color;
  ctx.textBaseline = 'top';

  const { x, y } = resolveTextPosition({
    positionX: layer.position.x,
    positionY: layer.position.y,
  });

  const { lines, isTruncated } = wrapText({
    text: bodyCopy,
    maxWidth: layer.maxWidth,
    maxLines: layer.maxLines,
    ctx,
  });

  const lineHeight = layer.lineHeight || layer.fontSize * 1.2;
  lines.forEach((line, index) => {
    const displayLine = index === layer.maxLines - 1 && isTruncated ? line + '...' : line;
    ctx.fillText(displayLine, x, y + index * lineHeight);
  });
};
